// src/api/client.ts
//
// Cliente fetch para el backend de CiudadApp (módulo seguridad).
// Adjunta el access token y normaliza { ok, data, error, message }.

import { useAuth } from '../stores/auth';

const BASE_URL =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (location.hostname === 'localhost' ? 'http://localhost/ciudadapp/api' : '/api');

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// Un único refresh en vuelo compartido: si varios requests fallan con 401 a la
// vez, todos esperan el mismo intento de refresh en lugar de disparar N.
let refreshPromise: Promise<boolean> | null = null;

async function refrescarSesion(): Promise<boolean> {
  const { refreshToken, user } = useAuth.getState();
  if (!refreshToken || !user) return false;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(BASE_URL + '/auth?action=refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.data?.access) return false;
        const u = useAuth.getState().user;
        if (!u) return false;
        useAuth.getState().setSession(json.data.access, json.data.refresh, u);
        return true;
      } catch {
        return false;
      }
    })();
    refreshPromise.finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit & { auth?: boolean; _retry?: boolean } = {}
): Promise<T> {
  const { auth = true, headers, _retry = false, ...rest } = options;
  const token = useAuth.getState().accessToken;

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(headers as Record<string, string> | undefined),
  };
  if (!(rest.body instanceof FormData) && rest.body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (auth && token) {
    finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(BASE_URL + path, { ...rest, headers: finalHeaders });

  let json: any;
  try {
    json = await res.json();
  } catch {
    throw new ApiError('Respuesta inválida del servidor', res.status, null);
  }

  if (!res.ok || json.ok === false) {
    // Access token vencido → intentar refrescar con el refresh token y reintentar
    // UNA vez. Solo si eso falla se cierra la sesión. Evita el "login en cada refresh".
    if (res.status === 401 && auth && !_retry) {
      const ok = await refrescarSesion();
      if (ok) return api<T>(path, { ...options, _retry: true });
      useAuth.getState().logout();
    }
    throw new ApiError(json.error || json.message || `HTTP ${res.status}`, res.status, json);
  }

  return json.data as T;
}

export function apiUrl(path: string): string {
  return BASE_URL + path;
}

/** Municipio efectivo del usuario (para pasar como ?municipio_id=). */
export function midParam(): string {
  const u = useAuth.getState().user;
  return u?.municipio_id ? `&municipio_id=${u.municipio_id}` : '';
}
